import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import * as authApi from "@/lib/api/auth";
import { onAccessTokenChange } from "@/lib/api/tokens";
import type { ApiUser } from "@/lib/api/types";

/**
 * Who is signed in, and whether they may be here at all.
 *
 * ── The role gate ──────────────────────────────────────────────────────────
 *
 * This is the difference between the panel's auth and the storefront's. A
 * customer signing in here authenticates perfectly well and must still be
 * refused, because `role !== "ADMIN"`.
 *
 * **The gate here is a courtesy, not the protection.** Every admin endpoint is
 * behind `requireRole("ADMIN")` on the server, which is what actually stops a
 * customer reading the order book — this only stops them staring at an empty
 * console wondering why nothing loads. Anyone reasoning about security should
 * read `arrow-be/src/middleware/auth.ts`, not this file.
 *
 * ── Why there are four states, not two ─────────────────────────────────────
 *
 * The access token lives in memory, so a reload always starts signed out as far
 * as JavaScript is concerned, and the refresh cookie is `HttpOnly` — no script
 * can read it. "Am I signed in" is a question only the server can answer, and
 * that answer takes a round trip. `loading` is that window; rendering a login
 * form during it would flash one at every returning operator on every reload.
 */

export type AuthStatus =
  | "loading"
  /** No session. */
  | "anonymous"
  /** Signed in, and an admin. */
  | "authorised"
  /** Signed in, but a customer. Authenticated and still not welcome. */
  | "forbidden";

type AuthContextValue = {
  status: AuthStatus;
  user: ApiUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (idToken: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const statusFor = (user: ApiUser | null): AuthStatus =>
  user === null ? "anonymous" : user.role === "ADMIN" ? "authorised" : "forbidden";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<ApiUser | null>(null);

  /** One attempt to trade the refresh cookie for a session, on first mount. */
  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      const result = await authApi.restoreSession();
      if (controller.signal.aborted) return;

      setUser(result?.user ?? null);
      setStatus(statusFor(result?.user ?? null));
    })();

    return () => controller.abort();
  }, []);

  /**
   * `apiFetch` clears the token on its own when a refresh fails mid-session —
   * a revoked family, for instance. Without this the panel would keep rendering
   * a signed-in shell over a session that has already ended.
   */
  useEffect(
    () =>
      onAccessTokenChange((token) => {
        if (token === null) {
          setUser(null);
          setStatus("anonymous");
        }
      }),
    [],
  );

  const adopt = useCallback((next: ApiUser) => {
    setUser(next);
    setStatus(statusFor(next));
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { user: next } = await authApi.login(email, password);
      adopt(next);
    },
    [adopt],
  );

  const signInWithGoogle = useCallback(
    async (idToken: string) => {
      const { user: next } = await authApi.signInWithGoogle(idToken);
      adopt(next);
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signIn, signInWithGoogle, signOut }),
    [status, user, signIn, signInWithGoogle, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");

  return context;
}
